// avatar — default skin module
// Token disposition: pass-through (no overrides)
// NO external imports — plain JavaScript only

export const styles = `
  :host {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    position: relative;
    box-sizing: border-box;
    flex-shrink: 0;

    --_size: var(--run-avatar-size, 36px);
    --_bg: var(--run-avatar-bg, var(--run-color-surface-subtle));
    --_fg: var(--run-avatar-fg, var(--run-color-text-secondary));
    --_border-color: var(--run-avatar-border-color, transparent);
    --_border-width: var(--run-avatar-border-width, 0);
    --_radius: var(--run-avatar-radius, var(--run-radius-semantic-pill));

    width: var(--_size);
    height: var(--_size);
    border-radius: var(--_radius);
    background: var(--_bg);
    color: var(--_fg);
    border: var(--_border-width) solid var(--_border-color);
    overflow: hidden;
    font-size: var(--run-font-size-sm);
    font-weight: var(--run-font-semantic-weight-medium);
    line-height: 1;
    user-select: none;
  }

  :host([size="xs"]) { --_size: var(--run-avatar-size, 20px); font-size: var(--run-font-size-xs); }
  :host([size="sm"]) { --_size: var(--run-avatar-size, 28px); font-size: var(--run-font-size-xs); }
  :host([size="md"]) { --_size: var(--run-avatar-size, 36px); font-size: var(--run-font-size-sm); }
  :host([size="lg"]) { --_size: var(--run-avatar-size, 48px); font-size: var(--run-font-size-base); }
  :host([size="xl"]) { --_size: var(--run-avatar-size, 64px); font-size: var(--run-font-size-lg); }

  :host([shape="square"]) { --_radius: var(--run-avatar-radius, var(--run-radius-semantic-control)); }
  :host([shape="circle"]) { --_radius: var(--run-avatar-radius, var(--run-radius-semantic-pill)); }

  [part="base"] {
    display: contents;
  }

  [part="fallback"] {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    position: absolute;
    inset: 0;
    color: var(--_fg);
  }

  [part="initials"] {
    display: none;
    font-size: inherit;
    font-weight: inherit;
    line-height: 1;
    color: inherit;
  }

  slot[name="icon"] {
    display: none;
    align-items: center;
    justify-content: center;
    width: 50%;
    height: 50%;
    color: inherit;
  }

  :host([data-state="initials"]) [part="initials"] {
    display: block;
  }

  :host([data-state="icon"]) slot[name="icon"] {
    display: flex;
  }

  [part="image"] {
    display: none;
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: absolute;
    inset: 0;
    opacity: 0;
    transition:
      opacity var(--run-motion-semantic-micro-duration, 150ms) var(--run-motion-semantic-micro-easing, ease);
  }

  :host([data-state="image"]) [part="image"] {
    display: block;
    opacity: 1;
  }

  :host([data-state="loading"]) [part="image"] {
    display: block;
    opacity: 0;
  }

  @media (prefers-reduced-motion: reduce) {
    [part="image"] {
      transition: none;
    }
  }
`;

const _placeholder_icon = `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" width="100%" height="100%">
  <circle cx="12" cy="8" r="4" fill="currentColor" opacity="0.9"/>
  <path d="M4 20c0-4 3.582-7 8-7s8 3 8 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" fill="none" opacity="0.9"/>
</svg>`;

export function render(host) {
  const { src, _imageState, _resolvedInitials, size, shape, loading } = host; // declared in MVA SkinModule.render contract

  const showImage = src != null && src !== '';
  const imgHtml = showImage
    ? `<img part="image" alt="" src="${src}" loading="${loading}">`
    : '';

  const initialsText = _resolvedInitials != null ? _resolvedInitials : '';

  host.shadowRoot.innerHTML = `
    <span part="base">
      <span part="fallback">
        <span part="initials">${initialsText}</span>
        <slot name="icon">${_imageState === 'icon' ? _placeholder_icon : ''}</slot>
      </span>
      ${imgHtml}
    </span>
  `;
}